import * as React from 'react';

import { InfoTooltip } from './Tooltip';
import { InlineSpinner } from '../assets/Spinner';
import {
  ChangelogItem,
  ChangelogOperation,
  CustomerChangelogItem,
  SystemChangelogItem,
} from '../pages/types';
import { formatDateTime } from '../utils';

const symbolMap = {
  CREATE: '➕',
  UPDATE: '✏️',
  DELETE: '❌',
};

const getCustomerChangelogMessage = (change: CustomerChangelogItem): string | null => {
  const { table, fieldName, newValue, operationType } = change;

  if (table === 'Claim') {
    if (operationType === ChangelogOperation.CREATE)
      return `created a new ${newValue} in the Claims table`;
    if (operationType === ChangelogOperation.UPDATE)
      return `updated ${newValue} in the Claims table`;
    if (operationType === ChangelogOperation.DELETE)
      return `deleted ${newValue} from the Claims table`;
    return null;
  }

  if (fieldName === 'new_date') return `skipped order, new order date is: ${newValue}`;
  if (fieldName === 'snooze') {
    // newValue is "<n> days: <prev date> >> <new date>"
    const separatorIndex = newValue.indexOf(': ');
    if (separatorIndex === -1) return `snoozed plan: ${newValue}`;
    const duration = newValue.slice(0, separatorIndex);
    const dates = newValue.slice(separatorIndex + 2);
    return `Snoozed plan ${duration}: updated order_date to ${dates} on the ${table} table`;
  }
  if (fieldName === 'rush_order_outcome')
    return `attempted to rush order and payment ${newValue.toLowerCase()}`;
  if (fieldName === 'primary_payment_processor')
    return `updated primary payment processor ${newValue}`;

  if (operationType === ChangelogOperation.CREATE) {
    return fieldName === 'id'
      ? `created new ${table} <${newValue}>`
      : `set new field ${fieldName} to ${newValue} on the ${table} table`;
  }
  if (operationType === ChangelogOperation.UPDATE) {
    return `updated ${fieldName} to ${newValue} on the ${table} table`;
  }
  if (operationType === ChangelogOperation.DELETE) {
    return `deleted <${newValue}> from the ${table} table`;
  }

  return null;
};

const FormattedCustomerChangelogItem = ({ change }: { change: CustomerChangelogItem }) => {
  const rowContent = getCustomerChangelogMessage(change);

  return (
    <div className="flex">
      <div className="mt-2 text-sm min-w-[200px]">{`[${formatDateTime(change.createdAt)}]`}</div>
      <div className="mt-2 text-sm">
        {symbolMap[change.operationType]} <b>{change.authorEmail}</b> {rowContent}{' '}
        <div className="ml-1 inline-flex">
          <InfoTooltip
            width="13"
            height="13"
            text={`Application: ${change.application ?? 'n/a'}, Primary Key: ${
              change.rowPrimaryKey
            }`}
          />
        </div>
      </div>
    </div>
  );
};

const getSystemChangelogMessage = (change: SystemChangelogItem): string | null => {
  const { category, operationType, newValue } = change;

  if (category === 'Item') return `created new item ${newValue}`;
  if (category === 'Run Command') return `ran ${newValue}`;

  if (category === 'Product Item') {
    if (operationType === 'CREATE') return `added product-item mapping for ${newValue}`;
    if (operationType === 'UPDATE') return `updated product-item mapping for ${newValue}`;
    return null;
  }

  if (category === 'Discount') {
    if (operationType === 'CREATE') return `created new discount code ${newValue}`;
    if (operationType === 'UPDATE') {
      const actionIcon = newValue.startsWith('ACTIVATED') ? '✅' : '🛑';
      return `${actionIcon} ${newValue}`;
    }
    return null;
  }

  if (category === 'Post Checkout Survey Option') {
    if (operationType === 'CREATE') return `created new PPS option ${newValue}`;
    if (operationType === 'UPDATE') return `updated ${newValue}`;
    return null;
  }

  if (category === 'Bulk Discount') {
    const [count, prefix, rest] = newValue.split(' ');
    if (count && prefix && rest) return `created ${count} codes with prefix ${prefix} ${rest}`;
    return null;
  }

  return null;
};

const FormattedSystemChangelogItem = ({ change }: { change: SystemChangelogItem }) => {
  const createdAt = change.createdAt.split('#')[0];
  const rowContent = getSystemChangelogMessage(change);

  return (
    <div className="flex">
      <div className="mt-2 text-sm min-w-[200px]">{`[${formatDateTime(createdAt)}]`}</div>
      <div className="mt-2 text-sm">
        {symbolMap[change.operationType]} <b>{change.authorEmail}</b> {rowContent}{' '}
        <div className="ml-1 inline-flex">
          <InfoTooltip
            width="13"
            height="13"
            text={`Application: ${change.application ?? 'n/a'}, ${change.category} ID: ${
              change.objectId
            }`}
          />
        </div>
      </div>
    </div>
  );
};

export const ChangelogList = ({
  type,
  isLoading,
  changelogError,
  changelogItems,
}: {
  type: 'customer' | 'system';
  isLoading: boolean;
  changelogError: string;
  changelogItems: ChangelogItem[];
}) => {
  return (
    <div className="mt-2 overflow-y-auto max-h-[400px] bg-white border rounded-lg p-4">
      {isLoading && <InlineSpinner />}
      {!isLoading && changelogError && <div>{changelogError}</div>}
      {!isLoading && !changelogError && changelogItems.length === 0 && (
        <div className="mt-2 text-sm">No Changelog to Display</div>
      )}
      {changelogItems.length !== 0 &&
        changelogItems.map((change: ChangelogItem, index) => {
          return type === 'customer' ? (
            <FormattedCustomerChangelogItem change={change as CustomerChangelogItem} key={index} />
          ) : (
            <FormattedSystemChangelogItem change={change as SystemChangelogItem} key={index} />
          );
        })}
    </div>
  );
};
